import { useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import {
  CustomerSummaryModal,
  type CustomerSummaryColumn,
  type CustomerSummarySectionAny,
} from '@/components/iem/shared/CustomerSummaryModal';
import type { TurnInSummaryItem } from './types';

export interface TurnInCustomerSummaryModalProps {
  open: boolean;
  onClose: () => void;
  unitName?: string;
  memberName?: string;
  serviceableItems: TurnInSummaryItem[];
  nonServiceableItems: TurnInSummaryItem[];
}

type TurnInSummaryRow = {
  key: string;
  name: string;
  lin: string | null;
  kitName: string | null;
  source: string;
  quantity: number;
};

const buildSummaryRows = (items: TurnInSummaryItem[]): TurnInSummaryRow[] => {
  const grouped = new Map<string, TurnInSummaryRow>();
  items.forEach((item) => {
    const name = item.name?.trim() ? item.name.trim() : 'Unknown item';
    const lin = item.lin?.trim() || null;
    const kitName = item.kitName?.trim() || null;
    const source = item.source?.trim() ? item.source.trim() : '-';
    const key = `${name}|${lin ?? ''}|${kitName ?? ''}|${source}`;
    const current = grouped.get(key);
    if (current) {
      current.quantity += 1;
    } else {
      grouped.set(key, { key, name, lin, kitName, source, quantity: 1 });
    }
  });

  return Array.from(grouped.values()).sort((a, b) => {
    if (a.name !== b.name) return a.name.localeCompare(b.name);
    return a.source.localeCompare(b.source);
  });
};

export const TurnInCustomerSummaryModal = ({
  open,
  onClose,
  unitName,
  memberName,
  serviceableItems,
  nonServiceableItems,
}: TurnInCustomerSummaryModalProps) => {
  const [generatedAt, setGeneratedAt] = useState<Date>(() => new Date());

  useEffect(() => {
    if (open) {
      setGeneratedAt(new Date());
    }
  }, [open]);

  const serviceableRows = useMemo(() => buildSummaryRows(serviceableItems), [serviceableItems]);
  const nonServiceableRows = useMemo(
    () => buildSummaryRows(nonServiceableItems),
    [nonServiceableItems],
  );

  const totals = useMemo(() => {
    const serviceableQty = serviceableItems.length;
    const nonServiceableQty = nonServiceableItems.length;
    return {
      serviceableQty,
      nonServiceableQty,
      overallQty: serviceableQty + nonServiceableQty,
    };
  }, [nonServiceableItems, serviceableItems]);

  const columns = useMemo<CustomerSummaryColumn<TurnInSummaryRow>[]>(
    () => [
      {
        header: 'LIN',
        width: '12%',
        render: (row) => <Typography variant="body2">{row.lin ?? '—'}</Typography>,
        printValue: (row) => row.lin ?? '—',
      },
      {
        header: 'Nomenclature',
        width: '48%',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
        ),
        printValue: (row) => ({ primary: row.name }),
      },
      {
        header: 'Source',
        width: '26%',
        render: (row) => <Typography variant="body2">{row.source || '-'}</Typography>,
        printValue: (row) => row.source || '-',
      },
      {
        header: 'Qty',
        width: '14%',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.quantity}
          </Typography>
        ),
        printValue: (row) => row.quantity,
      },
    ],
    [],
  );

  const sections = useMemo<CustomerSummarySectionAny[]>(
    () => [
      {
        key: 'serviceable',
        title: 'Serviceable Items',
        rows: serviceableRows,
        columns,
        getRowKey: (row: TurnInSummaryRow) => row.key,
      },
      {
        key: 'non-serviceable',
        title: 'Non-Serviceable Items',
        rows: nonServiceableRows,
        columns,
        getRowKey: (row: TurnInSummaryRow) => row.key,
      },
    ],
    [columns, nonServiceableRows, serviceableRows],
  );

  return (
    <CustomerSummaryModal
      open={open}
      onClose={onClose}
      title="Turn-In Summary"
      workflowLabel="Turn-In"
      unitName={unitName}
      memberName={memberName}
      sections={sections}
      generatedAt={generatedAt}
      summaryRight={
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Total Items: {totals.overallQty}
        </Typography>
      }
    />
  );
};
